

import StatBox from "../../components/StatBox";
import {
  UserPlus,
  DollarSign,
  Users,
  ThumbsUp,
  WalletCards,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Clock,
  Percent
} from "lucide-react";
import React, { useEffect, useState, Suspense, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAggregation,
  resetAggregation
} from "../../store/slices/aggregationSlice";
import { useSidebarContext } from "../global/sidebar/SidebarContext";
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer
} from "recharts";
import { useNavigate } from "react-router-dom";

const SimpleLineChart = React.lazy(() =>
  import("../../components/SimpleLineChart")
);
const RevenuePieChart = React.lazy(() =>
  import("../../components/CustomPieChart")
);
const MixBarChart = React.lazy(() => import("../../components/MixBarChart"));
const InboxAndUsers = React.lazy(() => import("./components/InboxAndUsers"));
const SalesAnalysisChart = React.lazy(() =>
  import("../../components/SalesAnalysisChart")
);
const InstallmentCollectionChart = React.lazy(() =>
  import("../../components/InstallmentCollectionChart")
);
const IncomeBreakdownPieChart = React.lazy(() =>
  import("../../components/IncomeBreakdownPieChart")
);
const CriticalAlerts = React.lazy(() => import("./components/CriticalAlerts"));
const MonthlyAgingReportChart = React.lazy(() => import("./components/MonthlyAgingReportChart"));
const QuickActions = React.lazy(() => import("./components/QuickActions"));
const RecentActivity = React.lazy(() => import("./components/RecentActivity"));
const UpcomingDueDates = React.lazy(() => import("./components/UpcomingDueDates"));
const FollowUpWidget = React.lazy(() => import("./components/FollowUpWidget"));

// Skeleton Components
const StatBoxSkeleton = () => (
  <div className="relative overflow-hidden rounded-xl bg-slate-800/60 backdrop-blur-sm border border-white/10 p-6 animate-pulse">
    <div className="flex items-center justify-between mb-4">
      <div className="w-12 h-12 rounded-xl bg-slate-700/50" />
      <div className="w-20 h-6 rounded-lg bg-slate-700/50" />
    </div>
    <div className="space-y-3">
      <div className="h-8 w-32 rounded-lg bg-slate-700/50" />
      <div className="h-4 w-40 rounded-lg bg-slate-700/50" />
    </div>
    <div className="mt-4 h-16 rounded-xl bg-slate-700/30" />
  </div>
);

const ChartSkeleton = ({ height = "260px" }) => (
  <div className={`relative overflow-hidden rounded-2xl bg-slate-800/60 backdrop-blur-xl border border-white/10 p-8 animate-pulse shadow-2xl`} style={{ height }}>
    <div className="space-y-4">
      <div className="h-6 w-48 rounded-lg bg-slate-700/50" />
      <div className="h-4 w-32 rounded-lg bg-slate-700/50" />
    </div>
    <div className="mt-6 space-y-3">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <div className="h-12 flex-1 rounded-xl bg-slate-700/30" />
        </div>
      ))}
    </div>
  </div>
);

const DashboardSkeleton = () => {
  const { theme } = useSidebarContext();

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Hero Section Skeleton */}
        <div className="relative mb-8">
          <div className="relative overflow-hidden rounded-3xl shadow-2xl backdrop-blur-xl border border-white/10 h-[200px] bg-slate-800/50 animate-pulse">
            <div className="absolute inset-0 flex flex-col sm:flex-row justify-between items-start sm:items-center p-6 sm:p-8 lg:p-10">
              <div className="space-y-3">
                <div className="h-10 w-72 rounded-lg bg-slate-700/50" />
                <div className="h-6 w-56 rounded-lg bg-slate-700/50" />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="space-y-8">
          {/* First Row - 4 Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
            {[...Array(4)].map((_, i) => (
              <StatBoxSkeleton key={`stat1-${i}`} />
            ))}
          </div>

          {/* Second Row - 3 Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-6">
            {[...Array(3)].map((_, i) => (
              <StatBoxSkeleton key={`stat2-${i}`} />
            ))}
          </div>

          {/* Third Row - 2 Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[...Array(2)].map((_, i) => (
              <StatBoxSkeleton key={`stat3-${i}`} />
            ))}
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7">
              <ChartSkeleton height="400px" />
            </div>
            <div className="lg:col-span-5">
              <ChartSkeleton height="400px" />
            </div>
          </div>

          {/* Bottom Widgets Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ChartSkeleton height="420px" />
            <ChartSkeleton height="420px" />
          </div>

          {/* Inbox Section */}
          <div className="group relative">
            <ChartSkeleton height="320px" />
          </div>
        </div>
      </div>
    </div>
  );
};

const createUnifiedTimeSeriesData = (data) => {
  if (!data) return [];

  const {
    financialTimeSeries = [],
    receivableTimeSeries = [],
    customerTimeSeries = [],
    bookedPlotsTimeSeries = [],
    availablePlotsTimeSeries = [],
    bookedMarlaTimeSeries = [],
    totalMarlaTimeSeries = [],
    combinedTimeSeries = [],
    timeSeriesData = [],
    overdueTimeSeries = [],
    cashFlowTimeSeries = [],
    collectionRateTimeSeries = [],
    pendingBookingsTimeSeries = []
  } = data;
  const dateMap = new Map();

  // Initialize all required fields for each date entry
  const initializeEntry = (date) => ({
    date,
    income: 0,
    totalPlotIncome: 0,
    expense: 0,
    netProfit: 0,
    totalCustomers: 0,
    totalReceivableAmount: 0,
    totalOverdueAmount: 0,
    cashFlowThisMonth: 0,
    monthlyCollectionRate: 0,
    totalBookedPlots: 0,
    newBookedPlots: 0,
    pendingBookingsCount: 0,
    totalAvailablePlots: 0,
    totalAvailableMarla: 0,
    totalBookedMarla: 0,
    newBookedMarla: 0,
    totalMarla: 0
  });

  if (timeSeriesData && timeSeriesData.length > 0) {
    timeSeriesData.forEach((item) => {
      const date = item.date;
      if (!dateMap.has(date)) {
        dateMap.set(date, initializeEntry(date));
      }
      const entry = dateMap.get(date);
      entry.totalAvailableMarla = item.totalAvailableMarla || 0;
      entry.income = item.income || 0;
      entry.expense = item.expense || 0;
      entry.netProfit = item.netProfit !== undefined ? item.netProfit : (entry.income - entry.expense);
      entry.totalCustomers = item.totalCustomers || 0;
      entry.totalReceivableAmount = item.receivableAmount || item.totalReceivableAmount || 0;
      entry.totalOverdueAmount = item.totalOverdueAmount || item.overdueAmount || 0;
      entry.cashFlowThisMonth = item.cashFlowThisMonth || item.cashFlow || 0;
      entry.monthlyCollectionRate = item.monthlyCollectionRate || item.collectionRate || 0;
      entry.totalBookedPlots = item.totalBookedPlots || 0;
      entry.totalAvailablePlots = item.totalAvailablePlots || 0;
      entry.pendingBookingsCount = item.pendingBookingsCount || item.pendingBookings || 0;
    });
  }
  financialTimeSeries.forEach((item) => {
    const date = item.dateKey || item._id || item.date;
    if (!dateMap.has(date)) {
      dateMap.set(date, initializeEntry(date));
    }
    const entry = dateMap.get(date);
    if (entry.income === 0) entry.income = item.totalIncome || item.income || 0;
    if (entry.totalPlotIncome === 0)
      entry.totalPlotIncome = item.totalPlotIncome || 0;
    if (entry.expense === 0) entry.expense = item.totalExpense || item.expense || 0;
    // Calculate netProfit if not provided
    entry.netProfit = item.netProfit !== undefined ? item.netProfit : (entry.income - entry.expense);
    entry.cashFlowThisMonth = item.cashFlowThisMonth || item.cashFlow || entry.cashFlowThisMonth || 0;
  });

  receivableTimeSeries.forEach((item) => {
    const date = item.dateKey || item._id || item.date;
    if (!dateMap.has(date)) {
      dateMap.set(date, initializeEntry(date));
    }
    const entry = dateMap.get(date);
    if (entry.totalReceivableAmount === 0) {
      entry.totalReceivableAmount = Math.max(0, item.receivableAmount || item.totalReceivableAmount || 0);
    }
  });

  // Process overdue time series
  overdueTimeSeries.forEach((item) => {
    const date = item.dateKey || item._id || item.date;
    if (!dateMap.has(date)) {
      dateMap.set(date, initializeEntry(date));
    }
    const entry = dateMap.get(date);
    entry.totalOverdueAmount = item.totalOverdueAmount || item.overdueAmount || entry.totalOverdueAmount || 0;
  });

  // Process cash flow time series
  cashFlowTimeSeries.forEach((item) => {
    const date = item.dateKey || item._id || item.date;
    if (!dateMap.has(date)) {
      dateMap.set(date, initializeEntry(date));
    }
    const entry = dateMap.get(date);
    entry.cashFlowThisMonth = item.cashFlowThisMonth || item.cashFlow || entry.cashFlowThisMonth || 0;
  });

  // Process collection rate time series
  collectionRateTimeSeries.forEach((item) => {
    const date = item.dateKey || item._id || item.date;
    if (!dateMap.has(date)) {
      dateMap.set(date, initializeEntry(date));
    }
    const entry = dateMap.get(date);
    entry.monthlyCollectionRate = item.monthlyCollectionRate || item.collectionRate || entry.monthlyCollectionRate || 0;
  });

  customerTimeSeries.forEach((item) => {
    const date = item.dateKey || item._id || item.date;
    if (!dateMap.has(date)) {
      dateMap.set(date, initializeEntry(date));
    }
    const entry = dateMap.get(date);
    if (entry.totalCustomers === 0) {
      entry.totalCustomers = item.newCustomers || item.totalCustomers || 0;
    }
  });

  bookedPlotsTimeSeries.forEach((item) => {
    const date = item.dateKey || item._id || item.date;
    if (!dateMap.has(date)) {
      dateMap.set(date, initializeEntry(date));
    }
    const entry = dateMap.get(date);
    entry.newBookedPlots = item.newBookedPlots || 0;
    if (entry.totalBookedPlots === 0) {
      entry.totalBookedPlots =
        item.newBookedPlots || item.totalBookedPlots || 0;
    }
  });

  // Process pending bookings time series
  pendingBookingsTimeSeries.forEach((item) => {
    const date = item.dateKey || item._id || item.date;
    if (!dateMap.has(date)) {
      dateMap.set(date, initializeEntry(date));
    }
    const entry = dateMap.get(date);
    entry.pendingBookingsCount = item.pendingBookingsCount || item.pendingBookings || entry.pendingBookingsCount || 0;
  });

  availablePlotsTimeSeries.forEach((item) => {
    const date = item.dateKey || item._id || item.date;
    if (!dateMap.has(date)) {
      dateMap.set(date, initializeEntry(date));
    }
    const entry = dateMap.get(date);
    if (entry.totalAvailablePlots === 0) {
      entry.totalAvailablePlots = item.totalAvailablePlots || 0;
    }
    if (entry.totalAvailableMarla === 0) {
      entry.totalAvailableMarla = item.totalAvailableMarla || 0;
    }
  });

  bookedMarlaTimeSeries.forEach((item) => {
    const date = item.dateKey || item._id || item.date;
    if (!dateMap.has(date)) {
      dateMap.set(date, initializeEntry(date));
    }
    const entry = dateMap.get(date);
    entry.newBookedMarla = item.newBookedMarla || 0;
    entry.totalBookedMarla = item.totalBookedMarla || item.newBookedMarla || entry.totalBookedMarla || 0;
  });

  const processedMarlaData = new Map();
  totalMarlaTimeSeries.forEach((item) => {
    const date = item.dateKey || item._id || item.date;
    if (!processedMarlaData.has(date)) {
      processedMarlaData.set(date, true);
      if (!dateMap.has(date)) {
        dateMap.set(date, initializeEntry(date));
      }
      const entry = dateMap.get(date);
      const marlaValue = item.totalMarla || 0;
      if (entry.totalMarla !== 0 && entry.totalMarla !== marlaValue) {
        console.warn(
          `Inconsistent totalMarla for ${date}: ${entry.totalMarla} vs ${marlaValue}`
        );
      }
      entry.totalMarla = marlaValue;
    }
  });
  combinedTimeSeries.forEach((item) => {
    const date = item.date || item.dateKey || item._id;
    if (!dateMap.has(date)) {
      dateMap.set(date, initializeEntry(date));
    }
    const entry = dateMap.get(date);
    if (entry.totalAvailablePlots === 0) {
      entry.totalAvailablePlots = item.totalAvailablePlots || 0;
    }
    // Ensure income, expense, and netProfit are set from combined time series
    if (entry.income === 0 && item.income) {
      entry.income = item.income;
    }
    if (entry.expense === 0 && item.expense) {
      entry.expense = item.expense;
    }
    if (item.netProfit !== undefined) {
      entry.netProfit = item.netProfit || (entry.income - entry.expense);
    }
    // Map additional fields from combined time series
    if (item.totalOverdueAmount !== undefined) {
      entry.totalOverdueAmount = item.totalOverdueAmount || entry.totalOverdueAmount || 0;
    }
    if (item.cashFlowThisMonth !== undefined) {
      entry.cashFlowThisMonth = item.cashFlowThisMonth || entry.cashFlowThisMonth || 0;
    }
    if (item.monthlyCollectionRate !== undefined) {
      entry.monthlyCollectionRate = item.monthlyCollectionRate || entry.monthlyCollectionRate || 0;
    }
    if (item.pendingBookingsCount !== undefined) {
      entry.pendingBookingsCount = item.pendingBookingsCount || entry.pendingBookingsCount || 0;
    }
  });

  // Ensure totalOverdueAmount is populated from all sources
  // If time series data doesn't have overdue amounts, try to use the current value
  // This ensures the trend graph has data to display
  const hasOverdueData = Array.from(dateMap.values()).some(item => item.totalOverdueAmount > 0);
  if (!hasOverdueData && data?.totalOverdueAmount) {
    // Distribute the current totalOverdueAmount across dates to show a trend
    // Use a simple approach: put the value on the most recent date
    const sortedEntries = Array.from(dateMap.values()).sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
    if (sortedEntries.length > 0) {
      // Set the current value on the most recent date
      sortedEntries[0].totalOverdueAmount = data.totalOverdueAmount || 0;
      // For older dates, use a decreasing pattern to show a trend
      for (let i = 1; i < Math.min(sortedEntries.length, 14); i++) {
        sortedEntries[i].totalOverdueAmount = Math.max(0,
          (data.totalOverdueAmount || 0) * (1 - (i * 0.05))
        );
      }
    }
  }

  // Calculate netProfit for all entries that don't have it
  const result = Array.from(dateMap.values()).map(item => ({
    ...item,
    netProfit: item.netProfit !== undefined ? item.netProfit : (item.income - item.expense)
  })).sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );

  return result;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dispatch = useDispatch();
  const { societies } = useSelector((state) => state.societies);
  const { activeUser } = useSelector((state) => state.auth);
  const { data = {}, error, loading } = useSelector((state) => state.aggregation);
  const { activeSociety, gradient, theme } = useSidebarContext();
  const [selectedTimeFrame, setSelectedTimeFrame] = useState("Monthly");

  const [showCharts, setShowCharts] = useState(false);
  const [showBottomWidgets, setShowBottomWidgets] = useState(false);

  const isInitialMount = useRef(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // P1: Date Range Filter State
  const [selectedDateRange, setSelectedDateRange] = useState('30d'); // '7d', '30d', '90d', '1y', 'all'

  const handleTimeFrameChange = (value) => {
    setSelectedTimeFrame(value);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 20;
      setOffset({ x, y });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    if (activeSociety?._id) {
      dispatch(resetAggregation());
      if (activeUser?.role) {
        // P1: Pass date range to aggregation API
        const params = { range: selectedDateRange };
        dispatch(fetchAggregation({ societyId: activeSociety._id, params })).then(() => {
          setHasLoadedOnce(true);
          isInitialMount.current = false;
        });
      }
    }
  }, [dispatch, activeSociety?._id, activeUser?.role, selectedDateRange]);

  useEffect(() => {
    let t1 = setTimeout(() => setShowCharts(true), 0);
    let t2 = setTimeout(() => setShowBottomWidgets(true), 300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  const handleNavigation = (path, sidebarItem) => {
    navigate(path);
    sessionStorage.setItem("selectedMenuItem", sidebarItem);
    localStorage.setItem("selectedMenuItem", sidebarItem);
  };

  if (!activeSociety || (loading && !hasLoadedOnce) || (error && !hasLoadedOnce)) {
    return <DashboardSkeleton />;
  }

  const society = societies.find((soc) => soc._id === activeSociety._id);

  if (!society) {
    return <DashboardSkeleton />;
  }

  const aggregationData = data || {
    totalSoldPlots: 0,
    totalBookedPlots: 0,
    totalCustomers: 0,
    totalUser: 0,
    totalIncome: 0,
    totalPlotIncome: 0,
    totalExpense: 0,
    totalReceivableAmount: 0,
    availablePlotsCount: 0,
    totalAvailableMarla: 0,
    totalBookedMarla: 0,
    totalMarla: 0,
    timeSeriesData: []
  };

  const processedTimeSeriesData = createUnifiedTimeSeriesData(data);

  const chartData1 = () => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec"
    ];
    return months.map((month, index) => {
      const income = aggregationData.totalIncome * (1 + Math.sin(index / 3));
      const expense = aggregationData.totalExpense * (1 + Math.cos(index / 2));
      return {
        month,
        income,
        expense
      };
    });
  };

  const chartData2 = [
    {
      name: "Aggregates",
      totalSoldPlots: aggregationData.totalSoldPlots,
      totalBookedPlots: aggregationData.totalBookedPlots,
      totalCustomers: aggregationData.totalCustomers,
      totalUsers: aggregationData.totalUser
    }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-PK", {
      style: "decimal",
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getGradientStyle = () => {
    const backgroundClass =
      gradient || "bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800";
    return {
      background: `var(--tw-gradient-${backgroundClass})`,
      className: backgroundClass
    };
  };

  const gradientStyle = getGradientStyle();

  return (
    <>
      <div className="w-full min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Modern Hero Section */}
          <div className="relative mb-8">
            <div
              className="relative overflow-hidden rounded-3xl shadow-2xl backdrop-blur-xl border border-white/10"
              style={{
                background: `linear-gradient(135deg, ${theme.mui.bg2} 0%, ${theme.mui.bg1} 100%)`,
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
              }}
            >
              {/* Animated gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 via-transparent to-blue-500/10 animate-pulse"></div>

              <div className="relative px-6 sm:px-8 lg:px-10 py-8 sm:py-10 lg:py-12">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-1 h-12 bg-gradient-to-b from-emerald-400 to-blue-400 rounded-full"></div>
                      <div>
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-2">
                          Welcome back, <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">{activeUser?.username || "User"}</span>
                        </h1>
                        <p className="text-slate-300 text-lg sm:text-xl font-medium">
                          {society.name || "Society Name"}
                        </p>
                      </div>
                    </div>
                    <p className="text-slate-400 text-sm sm:text-base mt-2 ml-4">
                      Your comprehensive dashboard for managing housing society operations
                    </p>
                  </div>

                  {/* Critical Alerts - Modern Design */}
                  {activeUser?.role === "Admin" && (
                    <div className="w-full lg:w-auto lg:min-w-[320px]">
                      <Suspense fallback={
                        <div className="h-28 w-full rounded-2xl bg-slate-800/40 border border-white/10 animate-pulse backdrop-blur-sm"></div>
                      }>
                        <CriticalAlerts />
                      </Suspense>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Container */}
          <div className="space-y-8">
            {(activeUser?.role === "Admin" || activeUser?.role === "Employee") && (
              <>
                {/* ============================================
                    SECTION 1: KEY FINANCIAL METRICS (Consolidated)
                    ============================================ */}
                <div className="w-full">
                  {/* Section Header with Modern Design */}
                  <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 rounded-xl border border-emerald-500/30">
                            <DollarSign className="w-5 h-5 text-emerald-400" />
                          </div>
                          <div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-white">Financial Overview</h2>
                            <p className="text-sm text-slate-400 mt-1">Complete financial performance metrics</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="text-sm font-medium text-slate-300 whitespace-nowrap">Time Period:</label>
                        <select
                          value={selectedDateRange}
                          onChange={(e) => setSelectedDateRange(e.target.value)}
                          className="px-4 py-2.5 text-sm rounded-xl bg-slate-800/80 backdrop-blur-sm border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all hover:bg-slate-800/90 shadow-lg"
                        >
                          <option value="7d">Last 7 Days</option>
                          <option value="30d">Last 30 Days</option>
                          <option value="90d">Last 90 Days</option>
                          <option value="1y">Last Year</option>
                          <option value="all">All Time</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Primary Financial Metrics - 4 Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
                    <StatBox
                      className="w-full cursor-pointer"
                      onClick={() => handleNavigation("/installments-payment/manage-installments", "item-Payments")}
                      title={`${formatCurrency(Math.floor(aggregationData.totalReceivableAmount) || 0)}`}
                      subtitle="Total Receivable"
                      chartType="area"
                      icon={<WalletCards className="w-6 h-6 text-blue-500" />}
                      timeSeriesData={processedTimeSeriesData}
                      dataKey="totalReceivableAmount"
                      titleTooltip="Total amount pending from all installments"
                    />

                    <StatBox
                      className="w-full cursor-pointer"
                      onClick={() => handleNavigation("/installments-payment/overdue-payments", "item-Payments")}
                      title={`${formatCurrency(aggregationData.totalOverdueAmount || 0)}`}
                      subtitle="Overdue Amount"
                      chartType="bar"
                      icon={<AlertTriangle className="w-6 h-6 text-red-500" />}
                      timeSeriesData={processedTimeSeriesData}
                      dataKey="totalOverdueAmount"
                      titleTooltip="Total amount from installments past their due date"
                    />

                    <StatBox
                      className="w-full cursor-pointer"
                      onClick={() => handleNavigation("/income/transaction", "item-Finance")}
                      title={`${formatCurrency(aggregationData.cashFlowThisMonth || 0)}`}
                      subtitle="Cash Flow (This Month)"
                      chartType="area"
                      icon={(aggregationData.cashFlowThisMonth || 0) >= 0 ? (
                        <TrendingUp className="w-6 h-6 text-emerald-500" />
                      ) : (
                        <TrendingDown className="w-6 h-6 text-red-500" />
                      )}
                      timeSeriesData={processedTimeSeriesData}
                      dataKey="cashFlowThisMonth"
                      titleTooltip="Income minus expenses for current month"
                    />

                    <StatBox
                      className="w-full cursor-pointer"
                      title={`${(aggregationData.monthlyCollectionRate || 0).toFixed(1)}%`}
                      subtitle="Collection Rate"
                      chartType="line"
                      icon={<Percent className="w-6 h-6 text-purple-500" />}
                      timeSeriesData={processedTimeSeriesData}
                      dataKey="monthlyCollectionRate"
                      titleTooltip="Percentage of receivables collected this month"
                    />
                  </div>

                  {/* Secondary Financial Metrics - 4 Cards (Total Income hidden from Employee) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-6">
                    {/* Total Income - Admin Only */}
                    {activeUser?.role === "Admin" && (
                      <StatBox
                        className="w-full cursor-pointer"
                        onClick={() => handleNavigation("/income/transaction", "item-Finance")}
                        title={`${formatCurrency(aggregationData.totalIncome || 0)}`}
                        subtitle="Total Income"
                        chartType="area"
                        icon={<DollarSign className="w-6 h-6 text-emerald-500" />}
                        timeSeriesData={processedTimeSeriesData}
                        dataKey="income"
                        titleTooltip="Total income from all sources (filtered by selected period)"
                      />
                    )}

                    <StatBox
                      className="w-full cursor-pointer"
                      onClick={() => handleNavigation("/income/transaction", "item-Finance")}
                      title={`${formatCurrency(aggregationData.totalExpense || 0)}`}
                      subtitle="Total Expenses"
                      chartType="bar"
                      icon={<TrendingDown className="w-6 h-6 text-red-500" />}
                      timeSeriesData={processedTimeSeriesData}
                      dataKey="expense"
                      titleTooltip="Total expenses (filtered by selected period)"
                    />

                    <StatBox
                      className="w-full cursor-pointer"
                      onClick={() => handleNavigation("/income/transaction", "item-Finance")}
                      title={`${formatCurrency((aggregationData.totalIncome || 0) - (aggregationData.totalExpense || 0))}`}
                      subtitle="Net Profit"
                      chartType="area"
                      icon={((aggregationData.totalIncome || 0) - (aggregationData.totalExpense || 0)) >= 0 ? (
                        <TrendingUp className="w-6 h-6 text-emerald-500" />
                      ) : (
                        <TrendingDown className="w-6 h-6 text-red-500" />
                      )}
                      timeSeriesData={processedTimeSeriesData}
                      dataKey="netProfit"
                      titleTooltip="Total income minus total expenses"
                    />

                    <StatBox
                      className="w-full cursor-pointer"
                      onClick={() => handleNavigation("/income/transaction", "item-Finance")}
                      title={`${formatCurrency(aggregationData.totalPlotIncome || 0)}`}
                      subtitle="Income from Plots"
                      chartType="area"
                      icon={<DollarSign className="w-6 h-6 text-emerald-400" />}
                      timeSeriesData={processedTimeSeriesData}
                      dataKey="totalPlotIncome"
                      titleTooltip="Income specifically from plot sales and installments"
                    />
                  </div>
                </div>

                {/* ============================================
                    SECTION 2: BUSINESS OPERATIONS
                    ============================================ */}
                <div className="w-full">
                  {/* Section Header */}
                  <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl border border-blue-500/30">
                        <Users className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <h2 className="text-2xl sm:text-3xl font-bold text-white">Business Operations</h2>
                        <p className="text-sm text-slate-400 mt-1">Plots, customers, and inventory metrics</p>
                      </div>
                    </div>
                  </div>

                  {/* Primary Operations Metrics - 4 Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
                    <StatBox
                      className="w-full cursor-pointer"
                      onClick={() => handleNavigation("/housing-society-management/plots-list", "item-Society")}
                      title={aggregationData.availablePlotsCount || 0}
                      subtitle="Available Plots"
                      chartType="area"
                      icon={<UserPlus className="w-6 h-6 text-emerald-500" />}
                      timeSeriesData={processedTimeSeriesData}
                      dataKey="totalAvailablePlots"
                    />

                    <StatBox
                      className="w-full cursor-pointer"
                      onClick={() => handleNavigation("/plots-booking/booking-process", "item-Booking")}
                      title={aggregationData.totalBookedPlots || 0}
                      subtitle="Booked Plots"
                      chartType="line"
                      icon={<DollarSign className="w-6 h-6 text-blue-500" />}
                      timeSeriesData={processedTimeSeriesData}
                      dataKey="totalBookedPlots"
                    />

                    <StatBox
                      className="w-full cursor-pointer"
                      onClick={() => handleNavigation("/customer/information", "item-Customers")}
                      title={aggregationData.totalCustomers || 0}
                      subtitle="Total Customers"
                      chartType="area"
                      icon={<Users className="w-6 h-6 text-purple-500" />}
                      timeSeriesData={processedTimeSeriesData}
                      dataKey="totalCustomers"
                    />

                    <StatBox
                      className="w-full cursor-pointer"
                      onClick={() => handleNavigation("/housing-society-management/plots-list", "item-Society")}
                      title={aggregationData.totalMarla || 0}
                      subtitle="Total Marla"
                      chartType="bar"
                      icon={<ThumbsUp className="w-6 h-6 text-emerald-500" />}
                      timeSeriesData={processedTimeSeriesData}
                      dataKey="totalMarla"
                    />
                  </div>

                  {/* Marla Breakdown - 2 Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
                    <StatBox
                      className="w-full cursor-pointer"
                      title={aggregationData.totalAvailableMarla || 0}
                      subtitle="Available Marla"
                      chartType="bar"
                      icon={<ThumbsUp className="w-6 h-6 text-blue-500" />}
                      timeSeriesData={processedTimeSeriesData}
                      dataKey="totalAvailableMarla"
                    />

                    <StatBox
                      className="w-full cursor-pointer"
                      title={aggregationData.totalBookedMarla || 0}
                      subtitle="Booked Marla"
                      chartType="bar"
                      icon={<ThumbsUp className="w-6 h-6 text-purple-500" />}
                      timeSeriesData={processedTimeSeriesData}
                      dataKey="totalBookedMarla"
                    />
                  </div>
                </div>

                {/* ============================================
                    SECTION 3: TARGETS & ALERTS + SALES PERFORMANCE
                    ============================================ */}
                <div className="w-full">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                    {/* Left Column: Targets & Alerts */}
                    <div className="flex flex-col space-y-6">
                      {/* Section Header for Left Column */}
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <div className="p-2 bg-gradient-to-br from-amber-500/20 to-amber-600/10 rounded-xl border border-amber-500/30">
                            <AlertTriangle className="w-5 h-5 text-amber-400" />
                          </div>
                          <div>
                            <h2 className="text-xl font-bold text-white">Targets & Alerts</h2>
                            <p className="text-xs text-slate-400 mt-0.5">Collection targets and notifications</p>
                          </div>
                        </div>
                      </div>

                      {/* Collection Target - Compact & Sexy Design */}
                      <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500/15 via-emerald-600/10 to-emerald-500/15 p-5 sm:p-6 border border-emerald-500/30 hover:border-emerald-500/50 transition-all duration-300 shadow-xl backdrop-blur-xl hover:shadow-emerald-500/10">
                        {/* Animated Background Gradient */}
                        <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/5 via-transparent to-emerald-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2"></div>

                        <div className="relative z-10">
                          {/* Compact Header */}
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-gradient-to-br from-emerald-400/30 to-emerald-500/20 rounded-xl border border-emerald-400/40 shadow-md group-hover:scale-105 transition-transform duration-300">
                                <TrendingUp className="w-5 h-5 text-emerald-300" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="text-sm font-bold text-white">Collection Target</h3>
                                  <span className="text-[10px] text-emerald-300/70 cursor-help bg-emerald-500/20 px-1.5 py-0.5 rounded border border-emerald-400/30" title="If target is set, tracks progress against target. If not set, tracks collections against installments due this month.">ⓘ</span>
                                </div>
                                <p className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                                  {(aggregationData.collectionTarget && aggregationData.collectionTarget > 0)
                                    ? formatCurrency(aggregationData.collectionTarget)
                                    : formatCurrency(aggregationData.monthlyReceivable || 0)}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="text-xl font-black text-emerald-400">
                                {Math.round(aggregationData.collectionTargetProgress || 0)}%
                              </span>
                              <p className="text-[10px] text-emerald-200/70 mt-0.5 font-medium">
                                {(aggregationData.collectionTarget && aggregationData.collectionTarget > 0)
                                  ? 'Target Set'
                                  : 'Due This Month'}
                              </p>
                            </div>
                          </div>

                          {/* Compact Progress Bar */}
                          <div className="mb-4">
                            <div className="relative h-2.5 bg-slate-800/60 rounded-full overflow-hidden border border-white/10">
                              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-emerald-400/20"></div>
                              <div
                                className="relative h-full bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-300 transition-all duration-700 ease-out shadow-md shadow-emerald-500/50"
                                style={{
                                  width: `${Math.min(100, Math.max(0, aggregationData.collectionTargetProgress || 0))}%`
                                }}
                              >
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                              </div>
                            </div>
                          </div>

                          {/* Compact Stats Grid */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-slate-800/40 rounded-lg p-3 border border-white/5 hover:bg-slate-800/60 transition-colors">
                              <p className="text-[10px] text-slate-400 font-medium mb-1">Collected</p>
                              <p className="text-base font-bold text-emerald-400">{formatCurrency(aggregationData.monthlyCollected || 0)}</p>
                            </div>
                            {(aggregationData.collectionTarget && aggregationData.collectionTarget > 0) && (
                              <div className="bg-slate-800/40 rounded-lg p-3 border border-white/5 hover:bg-slate-800/60 transition-colors">
                                <p className="text-[10px] text-slate-400 font-medium mb-1">Due This Month</p>
                                <p className="text-base font-bold text-white">{formatCurrency(aggregationData.monthlyReceivable || 0)}</p>
                              </div>
                            )}
                            {(!aggregationData.collectionTarget || aggregationData.collectionTarget === 0) && (
                              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 backdrop-blur-sm">
                                <div className="flex items-center gap-2">
                                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                                  <p className="text-[10px] text-amber-300/90 font-medium leading-tight">
                                    Set target in settings
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Low Inventory Alert - Compact Design */}
                      {aggregationData.isLowInventory && (
                        <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-500/15 via-red-600/10 to-red-500/15 p-5 sm:p-6 border border-red-500/30 hover:border-red-500/50 transition-all duration-300 shadow-xl backdrop-blur-xl hover:shadow-red-500/10">
                          {/* Animated Background Gradient */}
                          <div className="absolute inset-0 bg-gradient-to-br from-red-400/10 via-transparent to-red-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                          <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

                          <div className="relative z-10">
                            {/* Compact Header */}
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-gradient-to-br from-red-400/30 to-red-500/20 rounded-xl border border-red-400/40 shadow-md group-hover:scale-105 transition-transform duration-300">
                                  <AlertTriangle className="w-5 h-5 text-red-300" />
                                </div>
                                <div>
                                  <h3 className="text-sm font-bold text-red-300">Low Inventory Alert</h3>
                                  <p className="text-xl sm:text-2xl font-black text-red-400 tracking-tight">
                                    {aggregationData.availablePlotsCount || 0} Available
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Category Breakdown - Compact */}
                            <div className="space-y-2 bg-slate-900/40 rounded-xl p-4 border border-white/10 backdrop-blur-sm">
                              <p className="text-xs text-red-300/90 font-semibold mb-2 flex items-center gap-2">
                                <div className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse"></div>
                                Category Breakdown
                              </p>
                              <div className="space-y-2">
                                {aggregationData.lowInventoryCategories?.map((cat, idx) => (
                                  <div key={idx} className="flex items-center justify-between bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-lg p-2.5 transition-all">
                                    <span className="text-xs font-medium text-red-200">{cat.category || 'General'}</span>
                                    <span className="text-sm font-bold text-red-400 bg-red-500/20 px-2 py-1 rounded border border-red-400/30">
                                      {cat.availableCount} plots
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Installment Collection Chart - Below Targets & Alerts */}
                      <div className="bg-slate-800/60 backdrop-blur-xl rounded-2xl p-5 sm:p-6 border border-white/10 shadow-2xl hover:border-white/20 transition-all">
                        <Suspense fallback={<div className="h-[400px] rounded-xl bg-slate-800/40 animate-pulse" />}>
                          <InstallmentCollectionChart data={aggregationData.financialTimeSeries || []} />
                        </Suspense>
                      </div>
                    </div>

                    {/* Right Column: Monthly Sales Performance */}
                    <div className="flex flex-col h-full">
                      {/* Section Header for Right Column */}
                      <div className="mb-6">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="p-2 bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl border border-blue-500/30">
                            <TrendingUp className="w-5 h-5 text-blue-400" />
                          </div>
                          <div>
                            <h2 className="text-xl font-bold text-white">Sales Performance</h2>
                            <p className="text-xs text-slate-400 mt-0.5">Monitor sales, bookings, and growth</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-slate-800/60 backdrop-blur-xl rounded-2xl p-4 sm:p-5 border border-white/10 shadow-2xl hover:border-white/20 transition-all flex-1 min-h-[450px] flex flex-col">
                        <Suspense fallback={<div className="h-full rounded-xl bg-slate-800/40 animate-pulse" />}>
                          <SalesAnalysisChart data={processedTimeSeriesData} />
                        </Suspense>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Income Breakdown & Overdue Payments Analysis - Two Columns */}
                <div className="w-full mt-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                    {/* Left Column: Income Breakdown */}
                    <div className="bg-slate-800/60 backdrop-blur-xl rounded-2xl p-6 sm:p-8 border border-white/10 shadow-2xl hover:border-white/20 transition-all flex flex-col">
                      <Suspense fallback={<div className="h-[400px] rounded-xl bg-slate-800/40 animate-pulse" />}>
                        <IncomeBreakdownPieChart aggregationData={aggregationData} />
                      </Suspense>
                    </div>

                    {/* Right Column: Overdue Payments Analysis */}
                    <div className="bg-slate-800/60 backdrop-blur-xl rounded-2xl p-6 sm:p-8 border border-white/10 shadow-2xl hover:border-white/20 transition-all flex flex-col">
                      <div className="mb-6">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="p-2 bg-gradient-to-br from-orange-500/20 to-orange-600/10 rounded-xl border border-orange-500/30">
                            <AlertTriangle className="w-5 h-5 text-orange-400" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-white">Overdue Payments Analysis</h3>
                            <p className="text-sm text-slate-400 mt-1">Categorize overdue amounts by age (0-30 days, 31-60 days, 61-90 days, 90+ days) to prioritize collection efforts</p>
                          </div>
                        </div>
                      </div>
                      <Suspense fallback={<div className="h-[320px] rounded-xl bg-slate-800/40 animate-pulse" />}>
                        <MonthlyAgingReportChart />
                      </Suspense>
                    </div>
                  </div>
                </div>

                {/* ============================================
                    SECTION 4: CHARTS & ANALYSIS
                    ============================================ */}
                <div className="w-full">
                  {/* Section Header */}
                  <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-xl border border-purple-500/30">
                        <TrendingUp className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <h2 className="text-2xl sm:text-3xl font-bold text-white">Financial Analysis</h2>
                        <p className="text-sm text-slate-400 mt-1">Visual insights and trend analysis for better decision making</p>
                      </div>
                    </div>
                  </div>

                  {showCharts ? (
                    <div className="space-y-6">
                      {/* Top Row: Financial Trends & Revenue Distribution */}
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
                        <div className="lg:col-span-7 flex">
                          <div className="bg-slate-800/60 backdrop-blur-xl rounded-2xl p-6 sm:p-8 border border-white/10 shadow-2xl hover:border-white/20 transition-all w-full flex flex-col">
                            <div className="mb-6">
                              <h3 className="text-lg font-bold text-white mb-2">Income vs Expenses Trend</h3>
                              <p className="text-sm text-slate-400">Track your income and expenses over time to identify patterns and trends</p>
                            </div>
                            <div className="flex-1 min-h-[500px]">
                              <Suspense fallback={<div className="h-full rounded-xl bg-slate-800/40 animate-pulse" />}>
                                <SimpleLineChart
                                  data={aggregationData.financialTimeSeries || []}
                                  selectedTimeFrame={selectedTimeFrame}
                                  handleTimeFrameChange={handleTimeFrameChange}
                                />
                              </Suspense>
                            </div>
                          </div>
                        </div>
                        <div className="lg:col-span-5 flex">
                          <div className="bg-slate-800/60 backdrop-blur-xl rounded-2xl p-6 sm:p-8 border border-white/10 shadow-2xl hover:border-white/20 transition-all w-full flex flex-col">
                            <div className="mb-6">
                              <h3 className="text-lg font-bold text-white mb-2">Revenue Breakdown</h3>
                              <p className="text-sm text-slate-400">See how your income is distributed across different sources</p>
                            </div>
                            <div className="flex-1 min-h-[500px]">
                              <Suspense fallback={<div className="h-full rounded-xl bg-slate-800/40 animate-pulse" />}>
                                <RevenuePieChart aggregationData={aggregationData} />
                              </Suspense>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                        <div className="lg:col-span-7">
                          <div className="h-[250px] rounded-xl bg-slate-800/30 animate-pulse" />
                        </div>
                        <div className="lg:col-span-5">
                          <div className="h-[250px] rounded-xl bg-slate-800/30 animate-pulse" />
                        </div>
                      </div>
                      <div className="h-[280px] rounded-xl bg-slate-800/30 animate-pulse" />
                      <div className="h-[280px] rounded-xl bg-slate-800/30 animate-pulse" />
                    </div>
                  )}
                </div>

                {/* ============================================
                    SECTION 5: RECENT ACTIVITY & UPCOMING DUE DATES
                    ============================================ */}
                <div className="w-full">
                  {/* Section Header */}
                  <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2.5 bg-gradient-to-br from-cyan-500/30 to-cyan-600/20 rounded-xl border border-cyan-500/40 shadow-lg">
                        <Clock className="w-6 h-6 text-cyan-400" />
                      </div>
                      <div>
                        <h2 className="text-2xl sm:text-3xl font-bold text-white">Activity & Planning</h2>
                        <p className="text-sm text-slate-400 mt-1">Track recent transactions and plan for upcoming collections</p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                    <Suspense fallback={<div className="h-[400px] rounded-xl bg-slate-800/30 animate-pulse" />}>
                      <RecentActivity />
                    </Suspense>
                    <Suspense fallback={<div className="h-[400px] rounded-xl bg-slate-800/30 animate-pulse" />}>
                      <UpcomingDueDates />
                    </Suspense>
                  </div>

                  {/* Follow-up Widget */}
                  <div className="mt-6">
                    <Suspense fallback={<div className="h-[400px] rounded-xl bg-slate-800/30 animate-pulse" />}>
                      <FollowUpWidget />
                    </Suspense>
                  </div>
                </div>

                {/* ============================================
                    SECTION 6: QUICK ACTIONS & USER MANAGEMENT
                    ============================================ */}
                <div className="w-full">
                  {/* Section Header */}
                  <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-gradient-to-br from-violet-500/20 to-violet-600/10 rounded-xl border border-violet-500/30">
                        <TrendingUp className="w-5 h-5 text-violet-400" />
                      </div>
                      <div>
                        <h2 className="text-2xl sm:text-3xl font-bold text-white">Quick Actions</h2>
                        <p className="text-sm text-slate-400 mt-1">Frequently used operations and shortcuts</p>
                      </div>
                    </div>
                  </div>
                  <Suspense fallback={<div className="h-[200px] rounded-2xl bg-slate-800/40 backdrop-blur-sm border border-white/10 animate-pulse" />}>
                    <QuickActions />
                  </Suspense>
                </div>

                {/* ============================================
                    SECTION 7: USER MANAGEMENT
                    ============================================ */}
                <div className="w-full">
                  {/* Section Header */}
                  <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-gradient-to-br from-indigo-500/20 to-indigo-600/10 rounded-xl border border-indigo-500/30">
                        <Users className="w-5 h-5 text-indigo-400" />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-2xl sm:text-3xl font-bold text-white">User Management</h2>
                        <p className="text-sm text-slate-400 mt-1">System users and members overview</p>
                      </div>
                    </div>
                  </div>
                  <Suspense fallback={<div className="h-[400px] rounded-2xl bg-slate-800/40 backdrop-blur-sm border border-white/10 animate-pulse" />}>
                    <InboxAndUsers />
                  </Suspense>
                </div>
              </>
            )}


            {/* Additional Charts Section (Legacy - only for roles other than Admin and Employee) */}
            {showBottomWidgets && activeUser?.role !== "Admin" && activeUser?.role !== "Employee" ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="group relative">
                    <div className="absolute inset-0 rounded-3xl overflow-hidden">
                      <div
                        className={`absolute inset-0 ${theme.tailwind.bgGradient} backdrop-blur-xl transition-all duration-500 group-hover:backdrop-blur-3xl`}
                      />
                      <div
                        className={`absolute inset-0 ${theme.tailwind.bgGradient1} opacity-0 group-hover:opacity-100 transition-opacity duration-700`}
                      />
                      <div
                        className={`absolute inset-0 ${theme.tailwind.bgGradient2} opacity-0 group-hover:opacity-100 transition-opacity duration-700`}
                      />
                    </div>
                    <div
                      className={`relative rounded-3xl p-8 border border-white/10 bg-gradient-to-b from-white/5 to-transparent shadow-2xl transition-all duration-500 ${theme.tailwind.groupHover2} ${theme.tailwind.shadowhover}`}
                    >
                      <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center space-x-4">
                          <h2
                            className={`text-3xl font-bold ${theme.tailwind.text} group-hover:from-emerald-200 group-hover:to-sky-200`}
                          >
                            Campaign
                          </h2>
                          <div className="flex space-x-2">
                            <div
                              className={`h-2 w-2 rounded-full ${theme.tailwind.bgGradient3} animate-pulse`}
                            />
                            <div
                              className={`h-2 w-2 rounded-full ${theme.tailwind.bgGradient4} animate-pulse delay-100`}
                            />
                            <div
                              className={`h-2 w-2 rounded-full ${theme.tailwind.bgGradient5} animate-pulse delay-200`}
                            />
                          </div>
                        </div>
                        <span
                          className={`px-3 py-1 text-xs font-medium text-emerald-200/80 bg-emerald-500/10 rounded-full border border-emerald-500/20 animate-pulse delay-200`}
                        >
                          Live Data
                        </span>
                      </div>
                      <div className="h-[280px] transform transition-all duration-500 group-hover:scale-[1.02]">
                        <ResponsiveContainer width="100%" height={280}>
                          <RadarChart
                            cx="50%"
                            cy="50%"
                            outerRadius="80%"
                            data={chartData1()}
                          >
                            <PolarGrid strokeOpacity={0.2} />
                            <PolarAngleAxis
                              dataKey="month"
                              tick={{ fill: "#94a3b8", fontSize: 12 }}
                            />
                            <PolarRadiusAxis stroke="#475569" />
                            <Radar
                              name="Income"
                              dataKey="income"
                              stroke="#10b981"
                              fill="#10b981"
                              fillOpacity={0.3}
                            />
                            <Radar
                              name="Expense"
                              dataKey="expense"
                              stroke="#0ea5e9"
                              fill="#0ea5e9"
                              fillOpacity={0.3}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-6 text-center">
                        <p
                          className={`${theme.tailwind.text} text-sm font-medium tracking-wide transition-all duration-500 group-hover:text-emerald-200/90`}
                        >
                          Includes extra misc expenditures and costs
                        </p>
                        <div
                          className={`mt-3 h-0.5 w-24 mx-auto bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700`}
                        />
                      </div>
                      <div className="mt-6 flex items-center justify-center space-x-6">
                        <div className="flex items-center space-x-2">
                          <div
                            className={`w-3 h-3 rounded-full bg-emerald-500/60`}
                          />
                          <span className={`text-sm text-slate-300`}>Income</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className={`w-3 h-3 rounded-full bg-sky-500/60`} />
                          <span className={`text-sm text-slate-300`}>
                            Expense
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="group relative">
                    <div className="absolute inset-0 rounded-3xl overflow-hidden">
                      <div
                        className={`absolute inset-0 ${theme.tailwind.bgGradient} backdrop-blur-xl transition-all duration-500 group-hover:backdrop-blur-3xl`}
                      />
                      <div
                        className={`absolute inset-0 ${theme.tailwind.bgGradient1} opacity-0 group-hover:opacity-100 transition-opacity duration-700`}
                      />
                      <div
                        className={`absolute inset-0 ${theme.tailwind.bgGradient2} opacity-0 group-hover:opacity-100 transition-opacity duration-700`}
                      />
                    </div>
                    <div className="relative rounded-3xl p-8 border border-white/10 bg-gradient-to-b from-white/5 to-transparent shadow-2xl transition-all duration-500 group-hover:border-emerald-500/20 group-hover:shadow-emerald-500/10">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-4">
                          <h2
                            className={`text-3xl font-bold ${theme.tailwind.text}`}
                          >
                            Sales Quantity
                          </h2>
                        </div>
                      </div>
                      <div className="mt-2 mb-6">
                        <p
                          className={`${theme.tailwind.text} text-sm font-medium tracking-wide transition-all duration-500 ${theme.tailwind.groupHover6}`}
                        >
                          Records of Plot Sales and Customers
                        </p>
                        <div
                          className={`mt-3 h-0.5 w-24 mx-auto ${theme.tailwind.bgr1} opacity-0 group-hover:opacity-100 transition-all duration-700`}
                        />
                      </div>
                      <div className="h-[280px] transform transition-all duration-500 group-hover:scale-[1.02]">
                        <Suspense
                          fallback={
                            <div className="h-[280px] rounded-xl bg-slate-800/30 animate-pulse" />
                          }
                        >
                          <MixBarChart data={chartData2} />
                        </Suspense>
                      </div>
                      <div className="mt-11 flex items-center justify-center space-x-6">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full bg-[#06b6d4]" />
                          <span className="text-sm text-slate-300">
                            Sold Plots
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full bg-[#14b8a6]" />
                          <span className="text-sm text-slate-300">
                            Booked Plots
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full bg-[#0ea5e9]" />
                          <span className="text-sm text-slate-300">
                            Customers
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="group relative p-3">
                  <div
                    className={`absolute inset-0 rounded-2xl ${theme.tailwind.bg9} backdrop-blur-xl transition-all duration-500 group-hover:backdrop-blur-2xl`}
                  />
                  <Suspense
                    fallback={
                      <div className="h-[320px] rounded-2xl bg-slate-800/30 animate-pulse" />
                    }
                  >
                    <InboxAndUsers />
                  </Suspense>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;